import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "..";
import type { RootState } from "../../store";
import type { ProfileTransformResponse } from "../Profile/profileAPI.types";
import type {
  AboLineageEntry,
  AboLineageParams,
  AboLineageResponse,
  AboProfileParams,
  AboProfileResponse,
  CreateNoteParams,
  CreateNoteResponse,
  DeleteNoteParams,
  DeleteNoteResponse,
  GetSelectedNoteParams,
  MetricsRequestParams,
  MetricsResponse,
  Note,
  NoteBaseRequestParams,
  NotesResponse,
  UpdateNoteParams,
} from "./AboProfileAPI.types";
import { transformAboProfileResponse } from "./aboProfileTransform";

export const buildAboProfileUrl = (
  aff: string,
  abo: string,
  locale: string,
  includePlatinumAndAbove?: boolean,
): string => {
  const queryParams = new URLSearchParams({ Aff: aff, Abo: abo, Locale: locale });
  if (includePlatinumAndAbove === true) {
    queryParams.set("includePlatinumAndAbove", "true");
  }
  return `/abo-profile/api/profile?${queryParams}`;
};

export const aboProfileAPI = createApi({
  reducerPath: "aboProfileAPI",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["Notes", "SeriesDataview"],
  endpoints: (builder) => ({
    getABOProfile: builder.query<ProfileTransformResponse, AboProfileParams>({
      queryFn: async ({ aff, abo, includePlatinumAndAbove }, api, extraOptions, baseQuery) => {
        const state = api.getState() as RootState;
        const locale = state.loginLocalStorage?.loginFields?.locale ?? "en-US";
        const result = await baseQuery({
          url: buildAboProfileUrl(aff, abo, locale, includePlatinumAndAbove),
          method: "GET",
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: transformAboProfileResponse(result.data as AboProfileResponse) };
      },
    }),
    getProfileNotes: builder.query<NotesResponse, NoteBaseRequestParams>({
      query: ({ affabo }) => {
        return `/abo-profile/api/notes?affAbo=${affabo}`;
      },
      providesTags: ["Notes"],
    }),
    getProfileNote: builder.query<Note, GetSelectedNoteParams>({
      query: ({ affabo, noteId }) => {
        return `/abo-profile/api/notes/${noteId}?affAbo=${affabo}`;
      },
    }),
    createProfileNote: builder.mutation<CreateNoteResponse, CreateNoteParams>({
      query: ({ affabo, content }) => ({
        url: `/abo-profile/api/notes`,
        method: "POST",
        body: {
          affAbo: affabo,
          content: content,
        },
      }),
      invalidatesTags: ["Notes"],
    }),
    updateProfileNote: builder.mutation<CreateNoteResponse, UpdateNoteParams>({
      query: ({ noteId, content }) => ({
        url: `/abo-profile/api/notes/${noteId}`,
        method: "PUT",
        body: {
          content: content,
        },
      }),
      invalidatesTags: ["Notes"],
    }),
    deleteProfileNote: builder.mutation<DeleteNoteResponse, DeleteNoteParams>({
      query: ({ noteId }) => ({
        url: `/abo-profile/api/notes/${noteId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Notes"],
    }),
    getSeriesDataview: builder.query<MetricsResponse, MetricsRequestParams>({
      query: ({ aff, abo, locale, dataViewID, months, market }) => {
        const queryParams = new URLSearchParams({
          Aff: aff.padStart(3, "0"),
          Abo: abo,
          Locale: locale,
          DataViewID: dataViewID,
          Market: market,
        });
        if (months) queryParams.set("Months", `${months}`);
        return `/abo-profile/api/metrics?${queryParams}`;
      },
      providesTags: ["SeriesDataview"],
    }),
    getAboLineage: builder.query<AboLineageResponse, AboLineageParams>({
      query: ({ aff, abo, IncludeGlobalMB, IsEdlos, EdlosRootAffAbo, EdlosRootDepth }) => {
        const queryParams = new URLSearchParams({ Aff: aff, Abo: abo });
        if (IncludeGlobalMB !== undefined) {
          queryParams.append("IncludeGlobalMB", String(IncludeGlobalMB));
        }
        if (IsEdlos !== undefined) {
          queryParams.append("IsEdlos", String(IsEdlos));
        }
        if (IsEdlos === true && EdlosRootAffAbo !== undefined) {
          queryParams.append("EdlosRootAffAbo", EdlosRootAffAbo);
        }
        if (IsEdlos === true && EdlosRootDepth !== undefined) {
          queryParams.append("EdlosRootDepth", String(EdlosRootDepth));
        }
        return `/abo-profile/api/lineage?${queryParams}`;
      },
      transformResponse: (response: AboLineageResponse | AboLineageEntry[]): AboLineageResponse =>
        Array.isArray(response) ? { lineage: response } : response,
    }),
  }),
});

export const {
  useGetABOProfileQuery,
  useGetProfileNotesQuery,
  useGetProfileNoteQuery,
  useCreateProfileNoteMutation,
  useDeleteProfileNoteMutation,
  useUpdateProfileNoteMutation,
  useGetSeriesDataviewQuery,
  useLazyGetABOProfileQuery,
  useLazyGetProfileNotesQuery,
  useLazyGetSeriesDataviewQuery,
  useGetAboLineageQuery,
  useLazyGetAboLineageQuery,
} = aboProfileAPI;
